"use client";

import { usePathname } from "next/navigation";
import { Navigation } from "@/components/navigation";
import { Footer } from "@/components/footer";
import { PublicViewTransition } from "@/components/public-view-transition";
import { EmailCapturePopupGate } from "@/components/email-capture-popup-gate";
// Direct module imports, not the "@/components/roma" barrel: the barrel also
// re-exports RomaDarkFunnelHomepage, which drags the whole Roma homepage
// (hero slides, FAQ, offer capture, analytics) into the client graph of every
// public route — /privacy and /accessibility included.
import { RomaDarkTheme } from "@/components/roma/roma-dark-theme";
import { RomaReserveSheetProvider } from "@/components/roma/roma-reserve-sheet-context";
import { buildHomepageCopy } from "@/lib/marketing-location-copy";
import {
  getPublicChromeForPathname,
} from "@/lib/public-chrome";
import type { PublicPageBrand } from "@/lib/public-brand-theme";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import { useBrowserPublicHost } from "@/lib/use-browser-public-host";
import { useBrowserPublicLocationId } from "@/lib/use-browser-public-location-id";

/**
 * Client chrome wrapper for the (public) shell.
 *
 * On /order (the ordering app) we strip the marketing Navigation, Footer,
 * announcement, and email-capture popup, and drop the `pt-site-header` offset
 * so the order layout's own sticky header pins at the true viewport top — the
 * single top bar, owner.com-style. /giveaway (the QR entry page) is also
 * chromeless. Every other public route keeps the full
 * marketing chrome and the header offset.
 *
 * The location provider, brand palette (data-public-page-brand), and the
 * skip-to-content link stay mounted in the server layout regardless of route,
 * so Roma/Tuscan host resolution and useMarketingLocationContext keep working.
 */
export function PublicChrome({
  initialPublicHost,
  initialPublicLocationId,
  popupEnabled,
  pageBrand,
  romaFunnelEnabled,
  draftPreview = false,
  cateringOrderingLive = false,
  children,
}: {
  initialPublicHost: string | undefined;
  initialPublicLocationId: string | undefined;
  popupEnabled: boolean;
  pageBrand: PublicPageBrand;
  romaFunnelEnabled?: boolean;
  draftPreview?: boolean;
  /** Server-resolved per request host; see PublicChromeOptions.cateringOrderingLive. */
  cateringOrderingLive?: boolean;
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const browserPublicHost = useBrowserPublicHost(initialPublicHost);
  const browserPublicLocationId = useBrowserPublicLocationId(initialPublicLocationId);
  const isOrderRoute = pathname === "/order" || pathname.startsWith("/order/");
  const chrome = getPublicChromeForPathname(
    pathname ?? "",
    {
      NEXT_PUBLIC_SITE_URL: browserPublicHost,
      NEXT_PUBLIC_LOCATION_ID: browserPublicLocationId,
    },
    { pageBrand, romaFunnelEnabled, draftPreview, cateringOrderingLive },
  );
  const romaReserveLocations =
    getPublicPageBrand(chrome.siteConfig) === "chefs-roma"
      ? (buildHomepageCopy(chrome.location).romaLocations ?? [])
      : [];

  if (draftPreview) {
    return (
      <>
        <RomaDarkTheme
          pageBrand={pageBrand}
          romaFunnelEnabled={romaFunnelEnabled}
        />
        <Navigation
          initialPublicHost={initialPublicHost}
          initialPublicLocationId={initialPublicLocationId}
          pageBrand={pageBrand}
          romaFunnelEnabled={romaFunnelEnabled}
          draftPreview
        />
        <main id="main-content" className="flex-1 pt-site-header">
          <PublicViewTransition>{children}</PublicViewTransition>
        </main>
        <Footer
          initialPublicHost={initialPublicHost}
          initialPublicLocationId={initialPublicLocationId}
          pageBrand={pageBrand}
          romaFunnelEnabled={romaFunnelEnabled}
          draftPreview
        />
      </>
    );
  }

  if (pathname === "/giveaway") {
    // QR giveaway landing: one mobile screen, no nav/footer/popup, but keep
    // the tenant theme (Roma dark funnel) mounted.
    return (
      <>
        <RomaDarkTheme
          pageBrand={pageBrand}
          romaFunnelEnabled={romaFunnelEnabled}
        />
        <main id="main-content" className="flex-1">
          {children}
        </main>
      </>
    );
  }

  if (isOrderRoute) {
    // Order app shell: no marketing nav/footer/popup, no header offset.
    return (
      <main id="main-content" className="flex-1">
        <PublicViewTransition>{children}</PublicViewTransition>
      </main>
    );
  }

  return (
    <RomaReserveSheetProvider locations={romaReserveLocations}>
      <RomaDarkTheme
        pageBrand={pageBrand}
        romaFunnelEnabled={romaFunnelEnabled}
      />
      <Navigation
        initialPublicHost={initialPublicHost}
        initialPublicLocationId={initialPublicLocationId}
        pageBrand={pageBrand}
        romaFunnelEnabled={romaFunnelEnabled}
        cateringOrderingLive={cateringOrderingLive}
      />
      <main id="main-content" className="flex-1 pt-site-header">
        <PublicViewTransition>{children}</PublicViewTransition>
      </main>
      <Footer
        initialPublicHost={initialPublicHost}
        initialPublicLocationId={initialPublicLocationId}
        pageBrand={pageBrand}
        romaFunnelEnabled={romaFunnelEnabled}
        cateringOrderingLive={cateringOrderingLive}
      />
      <EmailCapturePopupGate
        popupEnabled={popupEnabled}
        initialPublicHost={initialPublicHost}
      />
    </RomaReserveSheetProvider>
  );
}
